import React, { useMemo } from "react";

import { MarkdownWeBuild } from "@/components/MarkdownWeBuild";
import { Artefact } from "@/types/artefacts";
import {
  generateDocumentMetadata,
  formatWordCount,
  formatReadingTime,
} from "@/utils/documentUtils";

interface DocumentArtefactViewerProps {
  artefact: Artefact;
}

const isMarkdownContent = (content: string, title: string): boolean => {
  // Check title first for explicit markdown indicators
  const titleLower = title.toLowerCase();

  if (titleLower.includes("readme") || titleLower.includes(".md") || titleLower.endsWith("md")) {
    return true;
  }

  // More robust markdown pattern detection
  const markdownPatterns = [
    /^#{1,6}\s+/m, // ATX-style headings (# Header)
    /^.+\n[=-]{2,}\s*$/m, // Setext-style headings (underlined)
    /^[*+-]\s+/m, // Unordered lists (-, *, +)
    /^\d+\.\s+/m, // Ordered lists (1. )
    /^>\s+/m, // Blockquotes (>)
    /\[.+\]\(.+\)/, // Links [text](url)
    /```[\s\S]*```/, // Fenced code blocks
    /`[^`]+`/, // Inline code
    /\*\*[^*]+\*\*/, // Bold **text**
    /__[^_]+__/, // Bold __text__
    /\*[^*]+\*/, // Italic *text*
    /_[^_]+_/, // Italic _text_
    /^[*_-]{3,}\s*$/m, // Horizontal rules (---, ***, ___)
    /^\s{4,}|\t/, // Indented code blocks
  ];

  // Check if content matches any markdown patterns
  const hasMarkdownSyntax = markdownPatterns.some((pattern) => pattern.test(content));

  // If we detect markdown patterns, treat as markdown
  if (hasMarkdownSyntax) {
    return true;
  }

  // Default to markdown for ambiguous cases (safer fallback)
  // Plain text will still render fine in MarkdownWeBuild component
  return true;
};

export const DocumentArtefactViewer: React.FC<DocumentArtefactViewerProps> = ({ artefact }) => {
  const isMarkdown = isMarkdownContent(artefact.content, artefact.title);

  const documentMetadata = useMemo(() => {
    return generateDocumentMetadata(artefact.content, artefact.title);
  }, [artefact.content, artefact.title]);

  return (
    <div className="bg-default-100 dark:bg-default-100/50 rounded-lg border border-default-200 dark:border-default-700">
      <div className="text-xs text-default-600 dark:text-default-400 p-2 font-medium border-b border-default-200 dark:border-default-700">
        {isMarkdown ? "Markdown Document" : "Text Document"}
        {" • "}
        {formatWordCount(documentMetadata.wordCount)}
        {" • "}
        {formatReadingTime(documentMetadata.readingTime)}
        {documentMetadata.headingCount > 0 && (
          <>
            {" • "}
            {documentMetadata.headingCount} heading
            {documentMetadata.headingCount !== 1 ? "s" : ""}
          </>
        )}
        {artefact.metadata?.estimatedSize && (
          <>
            {" • "}
            {artefact.metadata.estimatedSize}
          </>
        )}
      </div>
      <div className="bg-content1 p-4">
        {isMarkdown ? (
          <MarkdownWeBuild>{artefact.content}</MarkdownWeBuild>
        ) : (
          <pre className="text-sm whitespace-pre-wrap break-words font-sans text-default-800 dark:text-white leading-relaxed">
            {artefact.content}
          </pre>
        )}
      </div>
    </div>
  );
};
